// Heap / Priority Queue — Basic vs Optimized code for every "Beginner Problems" drill listed
// in the Heap Explanation modal. Same shape as trieDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, reusing ApproachVisualizer's array-row support
// (a heap IS an array under the hood). Two drills cross-reference the curated problems.

const heapDrills = [
  {
    id: 'build-min-heap',
    title: 'Insert Values Into a Min-Heap',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Re-Sort the Whole Array on Every Insert',
        explanation: 'Append the new value to the end, then sort the entire array from scratch so the smallest ends up in front. Correct — the smallest value is always findable — but every single insert pays for a full sort, throwing away the fact that the array was already almost in order.',
        steps: [
          'Append the new value to the end of the array.',
          'Sort the entire array.',
          'The smallest value is now always at index 0.',
          'Every insert costs O(n log n), regardless of how "close to sorted" the array already was.',
        ],
        code:
`void insertBasic(List<Integer> heap, int val) {
    heap.add(val);
    Collections.sort(heap);
}`,
        complexity: { time: 'O(n log n) per insert', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Sift-Up (Bubble Up)',
        explanation: 'Append the new value to the end, then compare it ONLY against its direct parent, swapping upward as long as it\'s smaller. Since the rest of the array already satisfies the heap property, only the path from the new leaf up to the root can possibly be wrong — nothing else needs checking.',
        steps: [
          'Append the new value at the next free index.',
          'Compare it to its parent at index (i − 1) / 2.',
          'If it\'s smaller than its parent, swap them and repeat from the new position.',
          'Stop when it reaches the root, or its parent is no longer bigger.',
        ],
        visual: {
          arrays: [{ label: 'heap', values: [3, 5, 8] }],
          varNames: [],
          steps: [
            { snapshots: { heap: [3, 5, 8, 1] }, highlights: { heap: [3] }, note: 'Append 1 at index 3 (the next free slot).' },
            { snapshots: { heap: [3, 1, 8, 5] }, highlights: { heap: [1] }, note: 'Parent of index 3 is index 1 (value 5). 1 < 5 — swap. Now at index 1.' },
            { snapshots: { heap: [1, 3, 8, 5] }, highlights: { heap: [0] }, note: 'Parent of index 1 is index 0 (value 3). 1 < 3 — swap. Now at the root — sift-up stops.' },
          ],
        },
        code:
`void insert(List<Integer> heap, int val) {
    heap.add(val);
    int i = heap.size() - 1;
    while (i > 0 && heap.get(i) < heap.get(parent(i))) {
        Collections.swap(heap, i, parent(i));
        i = parent(i);
    }
}

private int parent(int i) { return (i - 1) / 2; }`,
        complexity: { time: 'O(log n) per insert', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'extract-min',
    title: 'Remove the Minimum From a Min-Heap',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Remove and Rebuild From Scratch',
        explanation: 'Remove the root (it\'s always the minimum), then rebuild the entire heap from whatever remains, from the ground up. Correct, but rebuilding the whole structure is far more work than the one broken spot actually needs.',
        steps: [
          'Read the root — that\'s the minimum, and the answer to return.',
          'Remove it from the array.',
          'Re-heapify the ENTIRE remaining array from scratch.',
          'Rebuilding everything costs O(n), even though only the root position actually needs fixing.',
        ],
        code:
`int extractMinBasic(List<Integer> heap) {
    int min = heap.get(0);
    heap.remove(0);
    heapify(heap);   // rebuild the whole thing
    return min;
}`,
        complexity: { time: 'O(n) per extraction', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Move the Last Element to the Root, Then Sift Down',
        explanation: 'Move the LAST element into the now-empty root position (keeping the tree shape complete with no gaps), then let it "sift down" — swapping with its smaller child repeatedly until it finds its correct resting place. Only the path it travels down needs touching.',
        steps: [
          'Read the root as the value to return.',
          'Move the last element in the array into the root position, and shrink the array by one.',
          'Compare the new root to its children; if either is smaller, swap with the smaller one.',
          'Repeat from the new position until neither child is smaller, or a leaf is reached.',
        ],
        visual: {
          arrays: [{ label: 'heap', values: [1, 3, 8, 5] }],
          varNames: [],
          steps: [
            { note: 'Root (1) is the value to return. Move the last element (5) into the root position, shrink the array.' },
            { snapshots: { heap: [5, 3, 8] }, highlights: { heap: [0] }, note: 'Array is now [5, 3, 8]. Compare index 0 (5) to its children: index 1 (3) and index 2 (8).' },
            { snapshots: { heap: [3, 5, 8] }, highlights: { heap: [0] }, note: '3 is smaller than 5 — swap. Now at index 1, which has no children (out of range) — sift-down stops. Extracted value: 1.' },
          ],
        },
        code:
`int extractMin(List<Integer> heap) {
    int min = heap.get(0);
    int last = heap.remove(heap.size() - 1);
    if (!heap.isEmpty()) {
        heap.set(0, last);
        siftDown(heap, 0);
    }
    return min;
}

private void siftDown(List<Integer> heap, int i) {
    int smallest = i, left = 2 * i + 1, right = 2 * i + 2;
    if (left < heap.size() && heap.get(left) < heap.get(smallest)) smallest = left;
    if (right < heap.size() && heap.get(right) < heap.get(smallest)) smallest = right;
    if (smallest != i) {
        Collections.swap(heap, i, smallest);
        siftDown(heap, smallest);
    }
}`,
        complexity: { time: 'O(log n) per extraction', space: 'O(log n) — recursion depth' },
      },
    ],
  },
  {
    id: 'kth-largest-in-array-drill',
    title: 'Kth Largest Element in an Array',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Kth Largest Element in an Array\" there for the complete step-by-step and both code versions. You can " +
      "also try the streaming version hands-on in the \"Kth Largest Tracker\" mode of the Interactive Practice playground.",
  },
  {
    id: 'heapify-array',
    title: 'Build a Heap From an Arbitrary Array',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Insert One Value at a Time',
        explanation: 'Start with an empty heap and insert every element from the input array one at a time, using the standard sift-up insertion. Correct, and each individual insert is efficient — but doing this n times adds up.',
        steps: [
          'Start with an empty heap.',
          'Insert each element from the input array, one at a time, using sift-up.',
          'Each insert costs O(log n) on its own.',
          'n inserts at O(log n) each totals O(n log n).',
        ],
        code:
`List<Integer> heapifyBasic(int[] arr) {
    List<Integer> heap = new ArrayList<>();
    for (int val : arr) {
        heap.add(val);
        int i = heap.size() - 1;
        while (i > 0 && heap.get(i) < heap.get((i - 1) / 2)) {
            Collections.swap(heap, i, (i - 1) / 2);
            i = (i - 1) / 2;
        }
    }
    return heap;
}`,
        complexity: { time: 'O(n log n)', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Bottom-Up Heapify',
        explanation: 'Instead of building up from an empty heap, start with the array exactly as given, and sift DOWN every non-leaf node, working from the last one backward to the root. This surprising reordering — bottom-up instead of top-down — brings the total cost down to O(n), not O(n log n), because most nodes processed are near the bottom and have very little distance left to sift.',
        steps: [
          'Find the last non-leaf node: index (n / 2) − 1.',
          'Sift that node down into place.',
          'Move to the previous index and repeat, working backward toward the root.',
          'By the time the root is processed, everything below it is already a valid heap — one pass, no rebuilding.',
        ],
        visual: {
          arrays: [{ label: 'array', values: [9, 4, 7, 1, 3, 8, 2] }],
          varNames: [],
          steps: [
            { highlights: { array: [2] }, note: 'Start at index 2 (value 7), the last non-leaf node. Children: index 5 (8), index 6 (2). Smallest child is 2 — swap.' },
            { snapshots: { array: [9, 4, 2, 1, 3, 8, 7] }, highlights: { array: [1] }, note: 'Move to index 1 (value 4). Children: index 3 (1), index 4 (3). Smallest child is 1 — swap.' },
            { snapshots: { array: [9, 1, 2, 4, 3, 8, 7] }, highlights: { array: [0] }, note: 'Move to index 0 (value 9). Children: index 1 (1), index 2 (2). Smallest child is 1 — swap, then keep sifting down.' },
            { snapshots: { array: [1, 9, 2, 4, 3, 8, 7] }, highlights: { array: [1] }, note: 'Now at index 1 (value 9). Children: index 3 (4), index 4 (3). Smallest child is 3 — swap.' },
            { snapshots: { array: [1, 3, 2, 4, 9, 8, 7] }, note: 'Index 4 has no children — sift-down stops. Every non-leaf node processed: the array is now a valid min-heap in one O(n) pass.' },
          ],
        },
        code:
`void heapify(int[] arr) {
    for (int i = arr.length / 2 - 1; i >= 0; i--) {
        siftDown(arr, i, arr.length);
    }
}

private void siftDown(int[] arr, int i, int size) {
    int smallest = i, left = 2 * i + 1, right = 2 * i + 2;
    if (left < size && arr[left] < arr[smallest]) smallest = left;
    if (right < size && arr[right] < arr[smallest]) smallest = right;
    if (smallest != i) {
        int temp = arr[i]; arr[i] = arr[smallest]; arr[smallest] = temp;
        siftDown(arr, smallest, size);
    }
}`,
        complexity: { time: 'O(n)', space: 'O(log n) — recursion depth' },
        interviewNote: "The O(n) bound (not O(n log n)) surprises most people the first time — it holds because the number of nodes at each height shrinks exponentially while the sift-down cost only grows linearly with height, and the sum of that product converges to O(n). Worth mentioning if asked to justify it.",
      },
    ],
  },
  {
    id: 'last-stone-weight',
    title: 'Last Stone Weight',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort Before Every Smash',
        explanation: 'Repeatedly sort the entire list of stones, smash the two heaviest together, and put any leftover weight back in. Correct, but re-sorting the whole list every single round is far more work than finding just the top two requires.',
        steps: [
          'Sort the stones in descending order.',
          'Take the two heaviest, and smash them together (their difference goes back in, if positive).',
          'Re-sort the whole list again for the next round.',
          'Repeat until 0 or 1 stones remain.',
        ],
        code:
`int lastStoneWeightBasic(int[] stones) {
    List<Integer> list = new ArrayList<>();
    for (int s : stones) list.add(s);

    while (list.size() > 1) {
        list.sort(Collections.reverseOrder());
        int first = list.remove(0);
        int second = list.remove(0);
        if (first != second) list.add(first - second);
    }
    return list.isEmpty() ? 0 : list.get(0);
}`,
        complexity: { time: 'O(n² log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — A Max-Heap',
        explanation: 'A max-heap always keeps the two heaviest stones instantly accessible at the top — no re-sorting needed between rounds. Pop twice, push the difference back if it\'s positive, repeat.',
        steps: [
          'Load every stone into a max-heap.',
          'Pop the two heaviest stones.',
          'If their weights differ, push the difference back into the heap.',
          'Repeat until 0 or 1 stones remain — the final stone (or 0) is the answer.',
        ],
        visual: {
          arrays: [{ label: 'stones (max-heap order)', values: [8, 7, 4, 2, 1, 1] }],
          varNames: [],
          steps: [
            { highlights: { 'stones (max-heap order)': [0, 1] }, note: 'Pop 8 and 7. Difference: 1. Push 1 back.' },
            { snapshots: { 'stones (max-heap order)': [4, 2, 1, 1, 1] }, highlights: { 'stones (max-heap order)': [0, 1] }, note: 'Pop 4 and 2. Difference: 2. Push 2 back.' },
            { snapshots: { 'stones (max-heap order)': [2, 1, 1, 1] }, highlights: { 'stones (max-heap order)': [0, 1] }, note: 'Pop 2 and 1. Difference: 1. Push 1 back.' },
            { snapshots: { 'stones (max-heap order)': [1, 1, 1] }, highlights: { 'stones (max-heap order)': [0, 1] }, note: 'Pop 1 and 1. They\'re equal — both destroyed, nothing pushed back.' },
            { snapshots: { 'stones (max-heap order)': [1] }, note: 'One stone remains: 1 — the final answer.' },
          ],
        },
        code:
`int lastStoneWeight(int[] stones) {
    PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Collections.reverseOrder());
    for (int s : stones) maxHeap.add(s);

    while (maxHeap.size() > 1) {
        int first = maxHeap.poll();
        int second = maxHeap.poll();
        if (first != second) maxHeap.add(first - second);
    }
    return maxHeap.isEmpty() ? 0 : maxHeap.peek();
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'k-closest-points-to-origin',
    title: 'K Closest Points to the Origin',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort Every Point by Distance',
        explanation: 'Compute every point\'s distance to the origin, sort all points by that distance, and take the first k. Correct, but sorting the ENTIRE list is more work than necessary when k is much smaller than the total number of points.',
        steps: [
          'Compute the squared distance from the origin for every point (no need for an actual square root — it doesn\'t change the ordering).',
          'Sort all points by that distance, ascending.',
          'Take the first k points.',
          'The full sort costs O(n log n) regardless of how small k is.',
        ],
        code:
`int[][] kClosestBasic(int[][] points, int k) {
    Arrays.sort(points, (a, b) -> (a[0]*a[0] + a[1]*a[1]) - (b[0]*b[0] + b[1]*b[1]));
    return Arrays.copyOfRange(points, 0, k);
}`,
        complexity: { time: 'O(n log n)', space: 'O(1) extra (beyond the output)' },
      },
      {
        name: 'Optimized — A Max-Heap Capped at Size K',
        explanation: 'Keep a max-heap of at most k points — the CURRENT k closest seen so far, with the FARTHEST of them always on top. For every new point, if it\'s closer than the heap\'s current farthest, evict the farthest and add the new one. The heap never holds more than k points at once.',
        steps: [
          'Keep a max-heap ordered by distance, capped at size k.',
          'For each point, add it to the heap.',
          'If the heap now holds more than k points, remove the farthest (the max — the root).',
          'After every point is processed, the heap holds exactly the k closest points.',
        ],
        code:
`int[][] kClosest(int[][] points, int k) {
    PriorityQueue<int[]> maxHeap = new PriorityQueue<>(
        (a, b) -> (b[0]*b[0] + b[1]*b[1]) - (a[0]*a[0] + a[1]*a[1])   // farthest on top
    );
    for (int[] point : points) {
        maxHeap.add(point);
        if (maxHeap.size() > k) {
            maxHeap.poll();   // discard the current farthest
        }
    }
    return maxHeap.toArray(new int[0][]);
}`,
        complexity: { time: 'O(n log k)', space: 'O(k)' },
        whenToUse: "This is the answer whenever k is expected to be much smaller than n — the heap never grows past size k, unlike sorting the entire input.",
      },
    ],
  },
  {
    id: 'find-median-drill',
    title: 'Find Median From a Data Stream',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Find Median From Data Stream\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'task-scheduler',
    title: 'Task Scheduler (CPU Cooldown)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Simulate Every Time Unit',
        explanation: 'Advance one time unit at a time. At each tick, scan every task type to find one that both has remaining work AND has finished its cooldown from the last time it ran, greedily preferring whichever has the most work left. Correct, but scanning every task type at every single tick adds up.',
        steps: [
          'Track each task type\'s remaining count and the last time it ran.',
          'At every time unit, scan all task types, picking the most frequent one that\'s off cooldown.',
          'Run it (or idle, if nothing is available), and advance time by one unit.',
          'Repeat until every task has been fully scheduled.',
        ],
        code:
`int leastIntervalBasic(char[] tasks, int n) {
    int[] counts = new int[26];
    for (char t : tasks) counts[t - 'A']++;
    int[] lastRun = new int[26];
    Arrays.fill(lastRun, -n - 1);

    int time = 0, remaining = tasks.length;
    while (remaining > 0) {
        int best = -1;
        for (int i = 0; i < 26; i++) {
            if (counts[i] > 0 && time - lastRun[i] > n) {
                if (best == -1 || counts[i] > counts[best]) best = i;
            }
        }
        if (best != -1) { counts[best]--; lastRun[best] = time; remaining--; }
        time++;
    }
    return time;
}`,
        complexity: { time: 'O(totalTime · 26)', space: 'O(26)' },
      },
      {
        name: 'Optimized — Max-Heap of Frequencies, With a Cooldown Queue',
        explanation: 'Keep a max-heap of task frequencies, always running the CURRENTLY most frequent available task. After a task runs, it can\'t run again for n cycles — hold it in a separate cooldown queue (alongside the cycle it becomes available again) instead of scanning every task type each tick.',
        steps: [
          'Count every task\'s frequency, and load them into a max-heap.',
          'Each cycle, pop the most frequent available task, decrement its count, and run it.',
          'If it still has remaining occurrences, put it in a cooldown queue with the cycle it can return to the heap.',
          'At the start of each cycle, check the cooldown queue — if a task\'s cooldown has expired, push it back into the heap.',
        ],
        code:
`int leastInterval(char[] tasks, int n) {
    int[] counts = new int[26];
    for (char t : tasks) counts[t - 'A']++;

    PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Collections.reverseOrder());
    for (int c : counts) if (c > 0) maxHeap.add(c);

    Queue<int[]> cooldown = new LinkedList<>();   // {remainingCount, availableAtCycle}
    int time = 0;
    while (!maxHeap.isEmpty() || !cooldown.isEmpty()) {
        time++;
        if (!maxHeap.isEmpty()) {
            int count = maxHeap.poll() - 1;
            if (count > 0) cooldown.add(new int[] { count, time + n });
        }
        if (!cooldown.isEmpty() && cooldown.peek()[1] == time) {
            maxHeap.add(cooldown.poll()[0]);
        }
    }
    return time;
}`,
        complexity: { time: 'O(totalTime · log 26) ≈ O(totalTime)', space: 'O(26)' },
        interviewNote: "The cooldown queue is what avoids rescanning all 26 possible tasks at every tick — a task only re-enters consideration exactly when its cooldown expires, checked in O(1) via the queue's front.",
        whenToUse: "This heap-plus-cooldown-queue shape generalizes to any \"greedily pick the most valuable available option, with a temporary lockout after picking it\" scheduling problem.",
      },
    ],
  },
  {
    id: 'merge-k-sorted-lists',
    title: 'Merge K Sorted Lists (or Arrays)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Concatenate Everything, Then Sort',
        explanation: 'Dump every element from every list into one big array, and sort the whole thing. Correct, but ignores the fact that each individual list was ALREADY sorted — the same wasted insight as merging two sorted arrays by sorting from scratch.',
        steps: [
          'Walk every list, copying all their elements into one flat array.',
          'Sort that array.',
          'The result is fully merged and sorted.',
          'Sorting from scratch costs O(N log N), throwing away that each input list was already ordered.',
        ],
        code:
`int[] mergeKSortedBasic(int[][] lists) {
    List<Integer> all = new ArrayList<>();
    for (int[] list : lists) {
        for (int val : list) all.add(val);
    }
    Collections.sort(all);
    return all.stream().mapToInt(Integer::intValue).toArray();
}`,
        complexity: { time: 'O(N log N) — N = total elements across all lists', space: 'O(N)' },
      },
      {
        name: 'Optimized — A Min-Heap of One "Current" Element Per List',
        explanation: 'Keep exactly one candidate from each list in a min-heap at a time — its own current front element. Repeatedly extract the smallest, append it to the result, and push that SAME list\'s next element in to replace it. The heap never holds more than k elements (k = number of lists), no matter how large N is.',
        steps: [
          'Push the first element of every list into a min-heap, tagged with which list (and position) it came from.',
          'Extract the minimum, and append its value to the result.',
          'Push the NEXT element from that same list into the heap, if one remains.',
          'Repeat until the heap is empty — every list contributes its elements in order.',
        ],
        visual: {
          arrays: [{ label: 'list A', values: [1, 4] }, { label: 'list B', values: [2, 3] }, { label: 'heap (value, list)', values: ['1(A)', '2(B)'] }],
          varNames: [],
          steps: [
            { highlights: { 'heap (value, list)': [0] }, note: 'Extract the min: 1 from list A. Push list A\'s next element, 4. Result so far: [1].' },
            { snapshots: { 'heap (value, list)': ['2(B)', '4(A)'] }, highlights: { 'heap (value, list)': [0] }, note: 'Extract the min: 2 from list B. Push list B\'s next element, 3. Result so far: [1, 2].' },
            { snapshots: { 'heap (value, list)': ['3(B)', '4(A)'] }, highlights: { 'heap (value, list)': [0] }, note: 'Extract the min: 3 from list B. List B is now exhausted — nothing to push back. Result so far: [1, 2, 3].' },
            { snapshots: { 'heap (value, list)': ['4(A)'] }, highlights: { 'heap (value, list)': [0] }, note: 'Extract the min: 4 from list A. List A is now exhausted too. Result so far: [1, 2, 3, 4].' },
            { snapshots: { 'heap (value, list)': [] }, note: 'Heap is empty — merge complete. Final result: [1, 2, 3, 4].' },
          ],
        },
        code:
`int[] mergeKSorted(int[][] lists) {
    // Each heap entry: { value, listIndex, elementIndex }
    PriorityQueue<int[]> minHeap = new PriorityQueue<>((a, b) -> a[0] - b[0]);
    for (int i = 0; i < lists.length; i++) {
        if (lists[i].length > 0) minHeap.add(new int[] { lists[i][0], i, 0 });
    }

    List<Integer> result = new ArrayList<>();
    while (!minHeap.isEmpty()) {
        int[] top = minHeap.poll();
        result.add(top[0]);
        int listIdx = top[1], elemIdx = top[2];
        if (elemIdx + 1 < lists[listIdx].length) {
            minHeap.add(new int[] { lists[listIdx][elemIdx + 1], listIdx, elemIdx + 1 });
        }
    }
    return result.stream().mapToInt(Integer::intValue).toArray();
}`,
        complexity: { time: 'O(N log k) — k = number of lists', space: 'O(k)' },
        interviewNote: "This generalizes Merge Two Sorted Lists (from the Linked List pattern) to k lists at once — the heap replaces manually comparing k front elements against each other on every step.",
        whenToUse: "Reach for this whenever many already-sorted sources need combining into one sorted output — the heap keeps only k candidates in play at any moment, regardless of how large the total data is.",
      },
    ],
  },
];

export default heapDrills;
